import { Link, useParams } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Image } from "@/src/lib/components/custom/Image";
import { Badge } from "@/src/lib/components/ui/badge";
import { Button } from "@/src/lib/components/ui/button";
import { Card } from "@/src/lib/components/ui/card";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/src/lib/components/ui/select";
import { Separator } from "@/src/lib/components/ui/separator";
import { useApi } from "@/src/lib/hooks/api/use-api";
import type { GetTypes } from "@/src/lib/types/api-types";
import Icon from "../../../lib/components/custom/Icon";
import { Textarea } from "../../../lib/components/ui/textarea";
import AdminLayout from "../layout";

export default function AdminOrderDetails() {
	const { t } = useTranslation();
	const { orderId } = useParams({ from: "/admin/orders/$orderId" });
	const [notes, setNotes] = useState("");
	const { orders } = useApi();

	// Fetch order statuses
	const { data: orderStatusesData } = orders.getStatuses();

	// Fetch order details
	const {
		data: orderData,
		isLoading,
		error,
	} = orders.getById({
		orderId: orderId,
	});

	if (isLoading) {
		return (
			<AdminLayout>
				<div className="text-center py-12">
					<Icon
						name="RefreshCw"
						className="w-12 h-12 animate-spin text-primary mx-auto mb-4"
					/>
					<h2 className="text-xl font-semibold text-gray-900 mb-2">
						{t("admin.order_details_page.loading_title")}
					</h2>
					<p className="text-gray-600">
						{t("admin.order_details_page.loading_description")}
					</p>
				</div>
			</AdminLayout>
		);
	}

	if (error || !orderData?.order) {
		return (
			<AdminLayout>
				<div className="text-center py-12">
					<Icon
						name="TriangleAlert"
						className="w-12 h-12 text-yellow-500 mx-auto mb-4"
					/>
					<h2 className="text-xl font-semibold text-gray-900 mb-2">
						{t("admin.order_details_page.not_found_title")}
					</h2>
					<p className="text-gray-600 mb-4">
						{t("admin.order_details_page.not_found_description")}
					</p>
					<Button asChild>
						<Link to="/admin/orders">
							{t("admin.order_details_page.back_to_orders")}
						</Link>
					</Button>
				</div>
			</AdminLayout>
		);
	}

	const order = orderData.order;

	const getStatusColor = (status: string) => {
		switch (status) {
			case "pending":
				return "bg-red-100 text-red-800 border-red-200";
			case "received":
				return "bg-yellow-100 text-yellow-800";
			case "preparing":
				return "bg-orange-100 text-orange-800";
			case "out-for-delivery":
				return "bg-purple-100 text-purple-800";
			case "delivered":
				return "bg-green-100 text-green-800";
			case "cancelled":
				return "bg-red-100 text-red-800";
			default:
				return "bg-gray-100 text-gray-800";
		}
	};

	const getStatusText = (status: string) => {
		switch (status) {
			case "pending":
				return t("admin.status.pending_unpaid");
			case "received":
				return t("admin.status.new_order");
			case "preparing":
				return t("admin.status.preparing");
			case "out-for-delivery":
				return t("admin.status.out_for_delivery");
			case "delivered":
				return t("admin.status.delivered");
			case "cancelled":
				return t("admin.status.cancelled");
			default:
				return status;
		}
	};

	const getPaymentStatusColor = (status: string) => {
		switch (status) {
			case "completed":
			case "succeeded":
				return "bg-green-100 text-green-800";
			case "pending":
				return "bg-yellow-100 text-yellow-800";
			case "failed":
				return "bg-red-100 text-red-800";
			case "refunded":
				return "bg-gray-100 text-gray-800";
			default:
				return "bg-gray-100 text-gray-800";
		}
	};

	const statusSteps = (orderStatusesData?.statuses || []) as string[];
	const currentStatusIndex = statusSteps.indexOf(order.status);

	const getNextStatus = (currentStatus: string): string | null => {
		const transitions = orderStatusesData?.transitions;
		if (!transitions) return null;
		const nextStatuses = transitions[currentStatus as keyof typeof transitions];
		return nextStatuses?.[0] || null;
	};

	const getPrevStatus = (currentStatus: string): string | null => {
		const transitions = orderStatusesData?.transitions;
		if (!transitions) return null;

		for (const [fromStatus, toStatuses] of Object.entries(transitions)) {
			if (
				Array.isArray(toStatuses) &&
				(toStatuses as string[]).includes(currentStatus)
			) {
				return fromStatus;
			}
		}
		return null;
	};

	const formatDateTime = (dateString: string) => {
		return new Date(dateString).toLocaleString([], {
			weekday: "short",
			month: "short",
			day: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	const updateOrderStatus = orders.updateStatus;

	// Type for order status from the statuses endpoint
	type OrderStatus = GetTypes["orders"]["getStatuses"] extends {
		statuses?: readonly (infer S)[];
	}
		? S
		: string;

	const handleStatusUpdate = (newStatus: string) => {
		// Warn when moving from pending (Stripe not confirmed) to any status other than cancelled
		if (order.status === "pending" && newStatus !== "cancelled") {
			const proceed = window.confirm(
				t("admin.order_details_page.payment_warning"),
			);
			if (!proceed) return;
		}
		const validStatus = newStatus as OrderStatus;
		updateOrderStatus.mutate(
			{ orderId: order.id.toString(), status: validStatus },
			{
				onSuccess: () => {
					console.log(`Order ${order.id} status updated to ${newStatus}`);
				},
				onError: (error) => {
					console.error("Failed to update order status:", error);
				},
			},
		);
	};

	const handlePrintReceipt = () => {
		window.print();
	};

	return (
		<AdminLayout>
			<div className="space-y-6">
				{/* Header */}
				<div className="flex items-center justify-between">
					<div className="flex items-center space-x-4">
						<Button variant="ghost" size="sm" asChild>
							<Link to="/admin/orders">
								<Icon name="ArrowLeft" className="w-4 h-4 mr-2" />
								{t("admin.order_details_page.back_to_orders")}
							</Link>
						</Button>
						<div>
							<h1 className="text-2xl font-bold text-gray-900">
								{t("admin.order_details_page.order_number", { id: order.id })}
							</h1>
							<p className="text-gray-600">
								{t("admin.order_details_page.placed_on", {
									date: formatDateTime(order.createdAt),
								})}
							</p>
						</div>
					</div>

					<div className="flex items-center space-x-3">
						<Badge className={getStatusColor(order.status)}>
							{getStatusText(order.status)}
						</Badge>
						<Button variant="outline" onClick={handlePrintReceipt}>
							<Icon name="Printer" className="w-4 h-4 mr-2" />
							{t("admin.order_details_page.print_receipt")}
						</Button>
					</div>
				</div>

				<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
					{/* Order Items & Details */}
					<div className="lg:col-span-2 space-y-6">
						{/* Order Items */}
						<Card className="p-6">
							<h2 className="text-lg font-semibold text-gray-900 mb-4">
								{t("admin.order_details_page.order_items_title")}
							</h2>
							<div className="space-y-4">
								{order.items?.map((item) => (
									<div
										key={item.id}
										className="flex flex-col p-4 border border-gray-200 rounded-lg gap-3"
									>
										<div className="flex items-start justify-between">
											<div className="flex items-start space-x-4 flex-1 min-w-0">
												{item.menuItem?.imageUrl ? (
													<Image
														src={item.menuItem.imageUrl}
														alt={item.menuItem.name}
														className="w-16 h-16 rounded-lg object-cover shrink-0"
													/>
												) : (
													<div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center shrink-0">
														<Icon
															name="Image"
															className="w-6 h-6 text-gray-500"
														/>
													</div>
												)}
												<div className="flex-1 min-w-0">
													<h3 className="font-medium text-gray-900">
														{item.menuItem?.name ||
															t("admin.order_card.no_items")}
													</h3>
													<p className="text-sm text-gray-600">
														{item.menuItem?.category
															? t("admin.order_details_page.category_label", {
																	category: item.menuItem.category,
																})
															: t("admin.order_details_page.category_na")}
													</p>
													<p className="text-sm text-gray-600">
														{item.menuSize?.sizeName
															? t("admin.order_details_page.size_label", {
																	size: item.menuSize.sizeName,
																	quantity: item.quantity,
																})
															: t("admin.order_details_page.size_na", {
																	quantity: item.quantity,
																})}
													</p>

													{/* Extras Display */}
													{item.extras && item.extras.length > 0 && (
														<div className="flex flex-wrap gap-1.5 mt-2">
															{item.extras?.map((extra, idx: number) => (
																<Badge
																	key={`${extra.id}-${idx}`}
																	variant="secondary"
																	className="text-xs px-2 py-0.5 bg-amber-50 text-amber-800 border-amber-200"
																>
																	<Icon
																		name="Plus"
																		className="w-3 h-3 mr-1 inline"
																	/>
																	{extra.name}
																	{parseFloat(extra.unitPrice) > 0 &&
																		` +€${parseFloat(extra.unitPrice).toFixed(2)}`}
																	{extra.quantity > 1 && ` ×${extra.quantity}`}
																</Badge>
															))}
														</div>
													)}
												</div>
											</div>
											<div className="text-right shrink-0">
												<div className="font-medium text-gray-900">
													€{Number(item.totalPrice).toFixed(2)}
												</div>
												<div className="text-xs text-gray-500">
													€{Number(item.unitPrice).toFixed(2)} base
												</div>
											</div>
										</div>
									</div>
								)) || (
									<div className="text-center py-8 text-gray-500">
										{t("admin.order_details_page.no_items_found")}
									</div>
								)}
							</div>

							{order.deliveryInstructions && (
								<div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
									<div className="text-sm font-medium text-yellow-800 mb-1">
										{t("admin.order_details_page.special_instructions_label")}
									</div>
									<div className="text-sm text-yellow-700">
										{order.deliveryInstructions}
									</div>
								</div>
							)}
						</Card>

						{/* Status Timeline */}
						<Card className="p-6">
							<h2 className="text-lg font-semibold text-gray-900 mb-4">
								{t("admin.order_details_page.order_timeline")}
							</h2>
							<div className="space-y-4">
								{(statusSteps || []).map((step, index) => {
									const isCompleted = index < currentStatusIndex;
									const isCurrent = index === currentStatusIndex;

									return (
										<div key={step} className="flex items-center space-x-4">
											<div
												className={`w-8 h-8 rounded-full flex items-center justify-center ${
													isCompleted
														? "bg-green-100 text-green-600"
														: isCurrent
															? "bg-blue-100 text-blue-600"
															: "bg-gray-100 text-gray-400"
												}`}
											>
												<Icon
													name={
														isCompleted
															? "Check"
															: isCurrent
																? "Clock"
																: "Circle"
													}
													className="w-4 h-4"
												/>
											</div>
											<div className="flex-1">
												<div
													className={`font-medium ${isCompleted ? "text-green-600" : isCurrent ? "text-blue-600" : "text-gray-500"}`}
												>
													{getStatusText(step)}
												</div>
												{isCurrent && (
													<div className="text-sm text-gray-500">
														{t("admin.order_details_page.current_status")}
													</div>
												)}
											</div>
										</div>
									);
								})}
							</div>
						</Card>
					</div>

					{/* Order Summary & Actions */}
					<div className="space-y-6">
						{/* Order Summary */}
						<Card className="p-6">
							<h2 className="text-lg font-semibold text-gray-900 mb-4">
								{t("admin.order_details_page.order_summary")}
							</h2>

							<div className="space-y-3">
								<div className="flex justify-between text-sm">
									<span className="text-gray-600">
										{t("admin.order_details_page.subtotal")}
									</span>
									<span>
										$
										{(
											Number(order.totalPrice) - Number(order.deliveryFee ?? 0)
										).toFixed(2)}
									</span>
								</div>
								<div className="flex justify-between text-sm">
									<span className="text-gray-600">
										{t("admin.order_details_page.delivery")}
									</span>
									<span
										className={
											Number(order.deliveryFee ?? 0) === 0
												? "text-green-600"
												: ""
										}
									>
										{Number(order.deliveryFee ?? 0) === 0
											? t("admin.order_details.free")
											: `€${Number(order.deliveryFee).toFixed(2)}`}
									</span>
								</div>
								<Separator />
								<div className="flex justify-between font-semibold">
									<span>{t("admin.order_details_page.total")}</span>
									<span>€{Number(order.totalPrice).toFixed(2)}</span>
								</div>
							</div>

							<div className="mt-4 space-y-2">
								<div className="flex justify-between text-sm">
									<span className="text-gray-600">
										{t("admin.order_details_page.payment_method")}
									</span>
									<span
										className={`capitalize font-medium ${order.paymentMethod === "cash" ? "text-amber-600" : "text-green-600"}`}
									>
										{order.paymentMethod === "cash"
											? t("admin.order_card.cash_on_delivery")
											: t("admin.order_card.card")}
									</span>
								</div>
								<div className="flex justify-between text-sm">
									<span className="text-gray-600">
										{t("admin.order_details_page.payment_status")}
									</span>
									<Badge
										className={getPaymentStatusColor(
											order.paymentStatus || "succeeded",
										)}
									>
										{order.paymentStatus || "succeeded"}
									</Badge>
								</div>
								{order.paymentIntentId && (
									<div className="flex justify-between text-sm">
										<span className="text-gray-600">
											{t("admin.order_details_page.payment_intent_id")}
										</span>
										<span className="font-mono text-xs">
											{order.paymentIntentId}
										</span>
									</div>
								)}
							</div>
						</Card>

						{/* Customer Information */}
						<Card className="p-6">
							<h2 className="text-lg font-semibold text-gray-900 mb-4">
								{t("admin.order_details_page.customer_information")}
							</h2>

							<div className="space-y-3">
								<div>
									<div className="font-medium text-gray-900">
										{order.user?.name || t("admin.order_card.unknown_customer")}
									</div>
									<div className="text-sm text-gray-600">
										{order.user?.email || "N/A"}
									</div>
									<div className="text-sm text-gray-600">
										{order.user?.phone || "N/A"}
									</div>
								</div>

								<Separator />

								<div>
									<div className="text-sm font-medium text-gray-700 mb-1">
										{t("admin.order_details_page.delivery_address")}
									</div>
									<div className="text-sm text-gray-600">
										{order.address?.streetAddress || "N/A"}
										<br />
										{order.address?.city || "N/A"},{" "}
										{order.address?.state || "N/A"}{" "}
										{order.address?.zipCode || "N/A"}
									</div>
									{(order.address?.instructions ||
										order.deliveryInstructions) && (
										<div className="text-sm text-orange-600 mt-2">
											<strong>
												{t("admin.order_details_page.instructions_label")}
											</strong>{" "}
											{order.address?.instructions ||
												order.deliveryInstructions}
										</div>
									)}
								</div>

								<div className="flex justify-between text-sm">
									<span className="text-gray-600">
										{t("admin.order_details_page.order_date")}
									</span>
									<span>{formatDateTime(order.createdAt)}</span>
								</div>
							</div>

							<div className="mt-4 space-y-2">
								<Button variant="outline" className="w-full" size="sm">
									<Icon name="Phone" className="w-4 h-4 mr-2" />
									{t("admin.order_details_page.call_customer")}
								</Button>
								<Button variant="outline" className="w-full" size="sm">
									<Icon name="MessageCircle" className="w-4 h-4 mr-2" />
									{t("admin.order_details_page.send_message")}
								</Button>
							</div>
						</Card>

						{/* Status Management */}
						<Card className="p-6">
							<h2 className="text-lg font-semibold text-gray-900 mb-4">
								{t("admin.order_details_page.update_status")}
							</h2>

							<div className="space-y-3">
								{getNextStatus(order.status) &&
									(() => {
										const nextStatus = getNextStatus(order.status);
										return nextStatus ? (
											<Button
												className="w-full"
												onClick={() => handleStatusUpdate(nextStatus)}
											>
												<Icon name="ArrowRight" className="w-4 h-4 mr-2" />
												{t("admin.order_details_page.mark_as", {
													status: getStatusText(nextStatus),
												})}
											</Button>
										) : null;
									})()}

								<Select onValueChange={handleStatusUpdate}>
									<SelectTrigger>
										<SelectValue
											placeholder={t(
												"admin.order_details_page.change_status_manually",
											)}
										/>
									</SelectTrigger>
									<SelectContent>
										{(statusSteps || []).map((status) => (
											<SelectItem
												key={status}
												value={status}
												disabled={status === order.status}
											>
												{getStatusText(status)}
											</SelectItem>
										))}
									</SelectContent>
								</Select>

								{getPrevStatus(order.status) &&
									(() => {
										const prevStatus = getPrevStatus(order.status);
										return prevStatus ? (
											<Button
												variant="outline"
												className="w-full"
												onClick={() => handleStatusUpdate(prevStatus)}
											>
												<Icon name="ArrowLeft" className="w-4 h-4 mr-2" />
												{t("admin.order_details_page.revert_to", {
													status: getStatusText(prevStatus),
												})}
											</Button>
										) : null;
									})()}
							</div>
						</Card>

						{/* Internal Notes */}
						<Card className="p-6">
							<h2 className="text-lg font-semibold text-gray-900 mb-4">
								{t("admin.order_details_page.internal_notes")}
							</h2>
							<Textarea
								placeholder={t("admin.order_details_page.notes_placeholder")}
								value={notes}
								onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
									setNotes(e.target.value)
								}
								rows={3}
							/>
							<Button className="w-full mt-3" variant="outline" size="sm">
								{t("admin.order_details_page.save_notes")}
							</Button>
						</Card>
					</div>
				</div>
			</div>
		</AdminLayout>
	);
}
